import React, { useState } from 'react';
import { Wrench } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { formatMoney } from '../../utils/format';
import { toast } from '../ui/sonner';

const isLocked = (v) => v.status === 'delivery' || v.status === 'transfer' || v.status === 'selling';
const isOnMission = (v) => v.status === 'active' || !!v.assignedContractId;
export const needsRepair = (v) => (v.wear >= 15 || v.status === 'broken') && !isLocked(v);

/**
 * Flotte — bandeau « Réparer toute la flotte ». Retour apprenant : « la case
 * tout réparer n'a jamais fonctionné » → le coût affiché correspond aux
 * véhicules réellement réparés, les véhicules en mission sont listés à part
 * (case à cocher pour les inclure) et chaque clic renvoie un feedback.
 */
export default function RepairAllSection({ vehicles, treasury, maintainAllVehicles, playSound }) {
  const { t } = useTranslation();
  const [includeOnMission, setIncludeOnMission] = useState(false);
  const candidates = vehicles.filter(needsRepair);
  if (candidates.length === 0) return null;
  const onMission = candidates.filter(isOnMission);
  const free = candidates.filter((v) => !isOnMission(v));
  const targets = includeOnMission ? candidates : free;
  const cost = targets.reduce((s, v) => s + v.maintenancePerMonth, 0);
  const disabled = targets.length === 0 || treasury < cost;

  const run = () => {
    const res = maintainAllVehicles({ includeOnMission });
    if (res && res.ok) {
      playSound('repair');
      toast.success(`${res.count} véhicule${res.count > 1 ? 's' : ''} réparé${res.count > 1 ? 's' : ''} — ${formatMoney(res.cost)}`, {
        description: res.skippedCount > 0 ? `${res.skippedCount} en mission marqué${res.skippedCount > 1 ? 's' : ''} pour réparation au retour : ${res.skippedNames.join(', ')}` : 'Usure réduite de 40 points sur chaque véhicule.',
      });
    } else if (res && res.reason === 'insufficient_funds') {
      toast.error(`Trésorerie insuffisante : ${formatMoney(res.cost)} requis pour ${res.count} véhicule${res.count > 1 ? 's' : ''}.`);
    } else {
      toast.info('Aucun véhicule à réparer pour le moment.');
    }
  };

  return (
    <div className="bg-ink-800 rounded-lg border border-warn/40 p-3 space-y-2" data-testid="repair-all-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Wrench className="w-5 h-5 text-warn" />
          <div>
            <p className="text-sm font-semibold text-slate-200">{t('repair_all_fleet')}</p>
            <p className="text-[10px] text-slate-500" data-testid="repair-all-summary">
              {targets.length} véhicule{targets.length > 1 ? 's' : ''} à entretenir — Coût total : {formatMoney(cost)}
              {!includeOnMission && onMission.length > 0 ? ` · ${onMission.length} en mission (réparé${onMission.length > 1 ? 's' : ''} au retour)` : ''}
            </p>
          </div>
        </div>
        <button
          onClick={run}
          disabled={disabled}
          title={treasury < cost ? `Trésorerie insuffisante (${formatMoney(cost)} requis)` : undefined}
          className="text-xs bg-warn/100 text-white px-4 py-2 rounded-lg font-bold hover:bg-amber-600 disabled:opacity-40 transition-colors flex items-center gap-1.5"
          data-testid="repair-all-btn"
        >
          <Wrench className="w-3.5 h-3.5" /> Tout réparer
        </button>
      </div>
      {onMission.length > 0 && (
        <label className="flex items-center gap-2 text-[11px] text-slate-400 cursor-pointer">
          <input type="checkbox" checked={includeOnMission} onChange={(e) => setIncludeOnMission(e.target.checked)} className="accent-amber-500" data-testid="repair-all-include-mission" />
          Inclure les {onMission.length} véhicule{onMission.length > 1 ? 's' : ''} en mission (entretien sans immobilisation, comme le bouton Réparer)
        </label>
      )}
    </div>
  );
}
